import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import FlexLib from '@flex/lib';
import {
  Container,
  Grid,
  TextField,
  Backdrop,
  CircularProgress,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import config from '../../../constants/config';
import commonStrings from '../../../constants/commonStrings';
import autoFillMultipassFields from '../../../helpers/functions/autoFillCardFields';
import { changeFocus } from '../../../helpers/changeFocusCard';
import { getBinLookup } from '../../../services/binLookupService';
import invoicesService from '../../../services/invoicesService';
import transactionsService from '../../../services/transactionsService';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';
import AmountTextField from '../../../components/FeeDisplay/AmountTextField';
import TsepCardV2 from '../../../components/CardForm/TsepCard';
import PaytraceCard from '../../../components/CardForm/PaytraceCard';
import { BackButton } from '../../../components/Buttons';
import ACHForm from '../../../components/ACHForm/ACHForm';
import AddressFieldsDropDown from '../../../components/AddressFieldsDropDown/AddressFieldsDropDown2';
import FeeAmountTextField from '../../../components/FeeDisplay/FeeAmountTextField';
import PaxTerminalModal from './PaxTerminalModal';
import virtualTerminalConstants from '../../../constants/virtualTerminalConstants';
const { PAY_BUTTON_LABELS, PAY_TYPE_DEFAULTS, REDIRECT_PATHS, REDIRECT_DELAY_MS } = virtualTerminalConstants;

const feeService = new FlexLib.services.FeeService(console);
const mapDatabaseFeeSettings = FlexLib.mappers.feeService.mapDatabaseFeeSettings;

const OneTimeCustomerTab = () => {
  const [paymentGateway, setPaymentGateway] = useState('');

  // UI Control
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [paymentTypeToggle, setPaymentTypeToggle] = useState(commonStrings.card);
  const [payButtonLabel, setPayButtonLabel] = useState(PAY_BUTTON_LABELS.card);
  const [submitPayIsDisabled, setSubmitPayIsDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({
    invoice: false,
    customer: false,
    baseAmount: false,
    totalAmount: false,
  });
  const billingAddressFormValidationRef = React.useRef(null);
  const shippingAddressFormValidationRef = React.useRef(null);
  const [redirectTo, setRedirectTo] = useState('');

  // Settings
  const [achConfig, setAchConfig] = useState({
    isACHEnabled: false,
    confirmAccountNumber: false,
    isExtraFieldsRequired: false,
  });
  const [isPhoneNumberEnabled, setIsPhoneNumberEnabled] = useState(false);
  const [isDualPricingView, setIsDualPricingView] = useState(false);
  const [isPaxTransactionsEnabled, setIsPaxTransactionsEnabled] = useState(false);
  const [paxTerminals, setPaxTerminals] = useState([]);

  const [userData, setUserData] = useState({});
  const [paymentType, setPaymentType] = useState('');
  const [calculationResult, setCalculationResult] = useState({});
  const [displayCalculationResult, setDisplayCalculationResult] = useState({}); // same as calculation result unless dual pricing

  // Transaction Details
  const [baseAmount, setBaseAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState(''); // new invoice number attached to transaction
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const defaultAddress = {
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zip: '',
  };
  const [billingAddress, setBillingAddress] = useState(defaultAddress);
  const [shippingAddress, setShippingAddress] = useState(defaultAddress);
  const [notes, setNotes] = useState('');

  const [binCodesResponse, setBinCodesResponse] = useState({ bin: '', card: '', type: '' });
  const [tsepResponse, setTsepResponse] = useState({});

  // Payment data
  const [card, setCard] = useState({});
  const [cardErrors, setCardErrors] = useState({});
  const cardRef = useRef();
  const cscCardRef = useRef();
  const expiryDateRef = useRef();

  const initialAchDetails = {
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: '',
    bankName: '',
  };
  const [achDetails, setAchDetails] = useState(initialAchDetails);
  const achFormValidationRef = useRef(null);

  useEffect(() => {
    getSessionData();
  }, []);

  useEffect(() => {
    if (paymentGateway === commonStrings.multipass) {
      document.addEventListener('tsepEventUpdate', handleTsepEventUpdated);
    } else if (paymentGateway === commonStrings.paytrace) {
      let locationDetails = localStorage.getItem('locationPaydetails');
      locationDetails = JSON.parse(locationDetails) || {};
      if (locationDetails?.paytraceKey) {
        const localUrl = config.BASEURL + 'getPemFile?fileName=';
        window.paytrace.setKeyAjax(localUrl + locationDetails?.paytraceKey);
      }
    }

    return () => {
      document.removeEventListener('tsepEventUpdate', handleTsepEventUpdated);
    };
  }, [paymentGateway]);

  useEffect(() => {
    calculateFees();
  }, [baseAmount, userData?.settings, billingAddress?.state, paymentType]);

  useEffect(() => {
    if (messages?.length > 0) {
      window.scrollTo(0, 0);
    }
  }, [messages]);

  useEffect(() => {
    let timeoutId;
    if (redirectTo) {
      timeoutId = setTimeout(() => {
        history.push(redirectTo);
      }, REDIRECT_DELAY_MS);
    }

    return () => clearTimeout(timeoutId);
  }, [redirectTo]);

  useEffect(() => {
    // Disables one time card payment submission unless tsep response status is passing.
    if (
      paymentGateway === commonStrings.multipass &&
      paymentType !== commonStrings.ach &&
      tsepResponse?.status !== 'PASS'
    ) {
      setSubmitPayIsDisabled(true);
    } else {
      setSubmitPayIsDisabled(false);
    }
  }, [paymentGateway, paymentType, tsepResponse]);

  const getSessionData = () => {
    let location = localStorage.getItem('locationPaydetails');
    location = JSON.parse(location) || {};

    setPaymentGateway(location?.gateway);
    setIsPaxTransactionsEnabled(location?.paxTransactionEnable);
    setPaxTerminals([...(location?.paxTerminals || [])]);

    setAchConfig({
      isACHEnabled: location?.ACH,
      confirmAccountNumber: location?.confirmAccountNumber,
      isExtraFieldsRequired: location?.ACHFieldDisplay,
    });

    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setUserData(user);
    setIsDualPricingView(user?.settings?.DualFeeEnabled);
    setIsPhoneNumberEnabled(user?.settings?.isPhoneNumber);
  };

  const calculateFees = () => {
    const _baseAmount = baseAmount || '0.00';

    let result;
    try {
      if (!userData?.settings) {
        return {};
      }

      // Map database fees to fee config
      const { feeConfig, options } = mapDatabaseFeeSettings(userData?.settings ?? {});
      result = feeService.getCalculatedFees(
        _baseAmount,
        feeConfig,
        options,
        paymentType?.toLowerCase(),
        billingAddress?.state
      );

      setCalculationResult(result);

      if (options.dualPricing) {
        result = feeService.getDualPricingCalculatedFees(
          _baseAmount,
          feeConfig,
          options,
          paymentType?.toLowerCase(),
          billingAddress?.state
        );
      }
      setDisplayCalculationResult(result);
    } catch (e) {
      if (typeof setError === 'function' && e instanceof Error) {
        let erMsg = e.message;
        if (/fails to match the required pattern/.test(e?.message)) {
          erMsg =
            'Please verify values in Fee Settings. Percent fees can have 5 decimal places. Flat fees can have 2 decimal places.';
        }

        setMessages({
          severity: AlertSeverityTypes.error,
          message: `A fee calculation error has occurred: ${erMsg} If the error persists, contact techsupport@titaniumpayments.com or call 877-972-0700.`,
        });
      }
    }

    return result;
  };

  const getBinCodesResponse = async (cardBin) => {
    if (cardBin?.length >= 6) {
      getBinLookup(cardBin)
        .then((response) => {
          if (response?.paymentType) {
            setPaymentType(response.paymentType);
          }
          setBinCodesResponse(response?.binCodesResponse);
        })
        .catch((_error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Could not complete bin lookup. Please contact support.' },
          ]);
        });
    }
  };

  const handleChangePaytraceCard = (event) => {
    let { id, value } = event.target;

    setCardErrors({ cardNumber: false, cardExpiryDate: false, cardCsc: false });

    // Validate values
    if (id === 'cardNumber') {
      const cleanedNumber = value?.replace(/[_ ]/g, '');
      if (cleanedNumber.length >= 6) {
        getBinCodesResponse(cleanedNumber.slice(0, 6));
      }
    }
    if (value.length === 16 && value[value.length - 1] === '_' && id === 'cardNumber' && value[0] === '3') {
      value = value.slice(0, -1);
    }
    if (id === 'cardExpiryDate') {
      if (!/^(0[1-9]|1[0-2])([/-])?(\d{4})$/gm.test(value)) {
        setCardErrors((prevState) => ({ ...prevState, cardExpiryDate: true }));
      } else {
        setCard((prevState) => ({ ...prevState, cardExpiryDate: value }));
      }
    }
    changeFocus(id, value, expiryDateRef.current, cscCardRef.current);
  };

  const handleTsepEventUpdated = (event) => {
    const eventTsep = event.detail;
    setTsepResponse({ status: 'FAIL', tsepToken: '' });
    if (eventTsep.eventType === 'FieldValidationErrorEvent') {
      setMessages([{ severity: AlertSeverityTypes.error, message: eventTsep?.event?.message }]);
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'PASS') {
      setTsepResponse({ ...eventTsep.event });
      setMessages([]);
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'FAILURE') {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: `Please reload page to try again. ${eventTsep?.event?.message}`,
        },
      ]);
    } else if (eventTsep.eventType === 'FocusOutEvent' && eventTsep.event.fieldName === 'tsep-cardNum') {
      const cardNumberElement = document.getElementById('tsep-cardNum');
      if (cardNumberElement) {
        const cardNumberValue = cardNumberElement?.value.replace(/\s/g, '');
        getBinCodesResponse(cardNumberValue);
      }
    } else if (
      eventTsep.eventType === 'ErrorEvent' &&
      eventTsep.event.status === 'FAIL' &&
      eventTsep.event.responseCode === 'TSEPERR911'
    ) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Could not connect to Multipass. Please reload page to try again or contact support.',
        },
      ]);
    }
  };

  const handleChangeACH = (event) => {
    let { id, value } = event.target;

    setAchDetails((prevAch) => ({
      ...prevAch,
      [id]: value,
    }));
  };

  const getOneTimeCard = () => {
    let oneTimeCard = {
      encryptedCard: '',
      encryptedCSC: '',
      expirationDate: '',
    };

    if (paymentGateway === commonStrings.multipass && tsepResponse?.status === 'PASS') {
      oneTimeCard.encryptedCard = tsepResponse.tsepToken;
      oneTimeCard.encryptedCSC = tsepResponse.cvv2;
      oneTimeCard.expirationDate = tsepResponse.expirationDate;
    } else if (paymentGateway === commonStrings.paytrace) {
      window.paytrace.submitEncrypted('#DemoFormTerminal');
      oneTimeCard.encryptedCard = localStorage.getItem('0');
      oneTimeCard.encryptedCSC = localStorage.getItem('1');
      oneTimeCard.expirationDate = card?.cardExpiryDate;
      localStorage.removeItem('0');
      localStorage.removeItem('1');
    }

    return oneTimeCard;
  };

  const getPaymentDetails = () => {
    let isValid = true;
    let paymentType = '';
    let paymentDetails = {};

    switch (paymentTypeToggle) {
      case commonStrings.cash:
        paymentType = commonStrings.cash;
        break;
      case commonStrings.ach:
        paymentType = commonStrings.ach;
        if (achFormValidationRef.current?.validate()) {
          paymentDetails = {
            accountName: achDetails.accountName?.trim(),
            accountNumber: achDetails.accountNumber,
            routingNumber: achDetails.routingNumber,
            maskedAccountNumber: achDetails.accountNumber?.slice(-4),
            accountType: achDetails.accountType,
            bankName: achDetails.bankName,
          };
        } else {
          isValid = false;
        }
        break;
      case commonStrings.card: {
        const { encryptedCard, encryptedCSC, expirationDate } = getOneTimeCard();

        if (!encryptedCard) {
          isValid = false;
        }

        paymentType = binCodesResponse?.type?.toLowerCase();
        paymentDetails = {
          accountName: customerName?.trim(),
          cardNetwork: binCodesResponse?.card,
          binCodesResponse: binCodesResponse,
          encryptedCard,
          encryptedCSC,
          expirationDate,
        };
        break;
      }
      default:
        isValid = false;
        break;
    }

    if (!isValid || !paymentType) {
      setMessages((prevState) => [
        ...prevState,
        { severity: AlertSeverityTypes.error, message: 'No valid payment method for request.' },
      ]);
      return {};
    }
    paymentDetails.paymentType = paymentType;
    return paymentDetails;
  };

  /**
   * Process a transaction without an invoice - invoice number is just used for the ORDERID
   * Instead of creating an invoice to pay it
   */
  const processPaymentWithoutInvoice = async () => {
    setIsLoading(true);

    if (invoiceNumber) {
      try {
        const response = await invoicesService.checkIfInvoiceNumberIsUse(invoiceNumber);
        const isInUse = response?.data?.isInUse;
        if (typeof isInUse === 'boolean') {
          if (isInUse) {
            setMessages([
              {
                severity: AlertSeverityTypes.error,
                message: 'Invoice number is already in use. Please choose another.',
              },
            ]);
            setInvoiceNumber('');
            setIsLoading(false);
            return false;
          }
        }
      } catch (error) {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: error?.response?.data?.message ?? 'Could not determine if invoice number is valid.',
          },
        ]);
        setInvoiceNumber('');
        setIsLoading(false);
        return false;
      }
    }

    const paymentDetails = getPaymentDetails();
    if (!paymentDetails?.paymentType) {
      setMessages((prevState) => [
        ...prevState,
        {
          severity: AlertSeverityTypes.error,
          message: 'Payment could not be processed. Missing payment data.',
        },
      ]);
      setIsLoading(false);
      return false;
    }

    const payload = {
      source: 'Virtual Terminal',
      baseAmount,
      customer: {
        firstName: customerName?.trim(),
        email: customerEmail,
        phone: customerPhone,
      },
      orderId: invoiceNumber,
      billingAddress,
      shippingAddress,
      notes,
      paymentMethod: paymentDetails,
    };

    try {
      const response = await transactionsService.runTransaction(payload, { oneTimeCustomer: true });
      if (response?.data?.transactionId) {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Payment successful!' }]);
        setTimeout(() => {
          setRedirectTo(REDIRECT_PATHS.TRANSACTIONS);
        }, 2000);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        setMessages([{ severity: AlertSeverityTypes.error, message: error?.response?.data?.message }]);
      } else {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: 'Payment could not be processed. Please contact support.',
          },
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Validates basic fields based on what's required. Updates error state for component missing.
   * @param {boolean} isSavingInvoice - removes some required fields due to just saving invoice and not processing payment
   * @returns {boolean}
   */
  const areRequiredFieldsValid = (isSavingInvoice = false) => {
    let isValid = true;

    const newFieldErrors = {
      baseAmount: !baseAmount,
      customer: !customerName,
      totalAmount: !calculationResult?.totalAmount,
    };

    setFieldErrors(newFieldErrors);

    Object.values(newFieldErrors).forEach((value) => {
      isValid = isValid && !value;
    });

    if (!isSavingInvoice) {
      // Address validation updates own error state
      // Only applies if addresses are made visible
      if (billingAddressFormValidationRef.current) {
        isValid = isValid && billingAddressFormValidationRef.current.validate();
      }

      if (shippingAddressFormValidationRef.current) {
        isValid = isValid && shippingAddressFormValidationRef.current.validate();
      }
    }
    return isValid;
  };

  const handleSubmitPay = async () => {
    if (!areRequiredFieldsValid()) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Missing required data.',
        },
      ]);
      return;
    }

    await processPaymentWithoutInvoice();

    setIsLoading(false);
  };

  const handlePaxSaleRequest = async (serialNum) => {
    if (!serialNum) {
      setMessages([
        { severity: AlertSeverityTypes.warning, message: 'Please select a Pax terminal before submitting.' },
      ]);
      return;
    }

    if (!areRequiredFieldsValid(true)) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Missing required data.',
        },
      ]);
      return;
    }

    setIsLoading(true);

    invoicesService
      .sendPaxSaleRequest(
        {
          baseAmount,
          customer: {
            firstName: customerName?.trim(),
            email: customerEmail,
            phone: customerPhone,
          },
          orderId: invoiceNumber,
          billingAddress,
          shippingAddress,
          notes,
        },
        serialNum
      )
      .then(() => {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Pax sale request sent.' }]);
        setRedirectTo(REDIRECT_PATHS.INVOICES);
      })
      .catch((error) => {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: error?.response?.data?.message ?? 'Could not send Pax sale request.',
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePaymentTypeToggle = (event) => {
    setPaymentTypeToggle(event.target.value);
    setPayButtonLabel(PAY_BUTTON_LABELS[event.target.value]);
    setPaymentType(PAY_TYPE_DEFAULTS[event.target.value]);
  };

  return (
    <Container component="section" maxWidth={false}>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress style={{ color: 'white' }} />
      </Backdrop>
      {paymentGateway === 'multipass' && autoFillMultipassFields()}

      <Grid container rowSpacing={4} columnSpacing={4}>
        {messages?.length > 0 && (
          <Grid item xs={12} md={12}>
            <FlexAlerts messages={messages} />
          </Grid>
        )}
        <Grid item xs={12} lg={4} id="payment-details-section">
          <form id="DemoFormTerminal">
            <Stack spacing={2} alignItems="left">
              <ToggleButtonGroup
                exclusive
                color="primary"
                fullWidth
                value={paymentTypeToggle}
                onChange={handlePaymentTypeToggle}
              >
                <ToggleButton fullWidth value="card">
                  {`Credit/Debit${!isDualPricingView ? ' Card' : ''}`}
                </ToggleButton>
                {achConfig.isACHEnabled && (
                  <ToggleButton fullWidth value="ach">
                    {`ACH${!isDualPricingView ? ' Payment' : ''}`}
                  </ToggleButton>
                )}
                {isDualPricingView && (
                  <ToggleButton fullWidth value="cash">
                    Cash
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
              {paymentTypeToggle === commonStrings.card && paymentGateway === commonStrings.multipass && (
                <TsepCardV2
                  isOneTimeCustomer
                  displayCardDetails={{
                    showCardInput: true,
                    variant: 'block',
                  }}
                />
              )}
              {paymentTypeToggle === commonStrings.card && paymentGateway === commonStrings.paytrace && (
                <PaytraceCard
                  card={card}
                  cardErrors={cardErrors}
                  cardRef={cardRef}
                  expiryDateRef={expiryDateRef}
                  cscCardRef={cscCardRef}
                  handleChangeCard={handleChangePaytraceCard}
                  displayCardDetails={{
                    showCardInput: true,
                    variant: 'block',
                  }}
                />
              )}
              {paymentTypeToggle === commonStrings.ach && achConfig.isACHEnabled && (
                <div>
                  <ACHForm
                    ach={achDetails}
                    handleChangeACH={handleChangeACH}
                    variant={achConfig.isExtraFieldsRequired ? 'detailedList' : 'basicListTight'}
                    location={achConfig}
                    ref={achFormValidationRef}
                  />
                </div>
              )}
            </Stack>
          </form>
        </Grid>

        <Grid item xs={12} lg={8} id="transaction-details-section">
          <Stack spacing={3}>
            <TextField
              id="invoice-number-input"
              data-cy="customer-name-input"
              label={'Invoice Number'}
              fullWidth
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e?.target?.value)}
              error={fieldErrors?.invoice}
            />

            <TextField
              id="customer-name-input"
              data-cy="customer-name-input"
              label="Customer Name"
              fullWidth
              value={customerName}
              onChange={(e) => setCustomerName(e?.target?.value)}
              required
              error={fieldErrors?.customer}
            />

            <Stack spacing={2} direction="row">
              <AmountTextField
                id="base-amount"
                label={isDualPricingView ? 'Cash Amount' : 'Amount'}
                amount={baseAmount}
                setAmount={setBaseAmount}
                required
                fullWidth
                error={fieldErrors?.baseAmount}
              />
              {isDualPricingView && (
                <AmountTextField
                  id="card-subtotal"
                  label={'Card Amount'}
                  disabled
                  amount={displayCalculationResult?.card?.subTotal}
                  fullWidth
                />
              )}
            </Stack>

            <TextField
              id="customer-email"
              fullWidth
              label="Email"
              onChange={(event) => {
                setCustomerEmail(event?.target?.value);
              }}
              value={customerEmail}
            />
            {isPhoneNumberEnabled && (
              <TextField
                id="customer-phone"
                fullWidth
                label="Phone Number"
                onChange={(event) => {
                  setCustomerPhone(event?.target?.value);
                }}
                value={customerPhone}
              />
            )}
            {!isDualPricingView && !calculationResult?.options?.taxLast && (
              <FeeAmountTextField
                id="tax"
                disabled
                fee={calculationResult.fees?.tax}
                databaseFeeSettings={userData?.settings}
              />
            )}
            {isDualPricingView ? (
              <Stack spacing={2} direction="row">
                <FeeAmountTextField
                  id="cash-surcharge"
                  labelPrefix="Cash"
                  disabled
                  fee={displayCalculationResult.cash?.fees?.surcharge}
                  databaseFeeSettings={userData?.settings}
                />
                <FeeAmountTextField
                  id="card-surcharge"
                  labelPrefix="Card"
                  disabled
                  fee={displayCalculationResult.card?.fees?.surcharge}
                  databaseFeeSettings={userData?.settings}
                />
              </Stack>
            ) : (
              <FeeAmountTextField
                id="surcharge"
                disabled
                fee={calculationResult.fees?.surcharge}
                databaseFeeSettings={userData?.settings}
              />
            )}
            {!isDualPricingView && (
              <>
                <FeeAmountTextField
                  id="card-upcharge"
                  disabled
                  fee={calculationResult.fees?.cardUpcharge}
                  databaseFeeSettings={userData?.settings}
                />
                {[commonStrings.ach, commonStrings.cash].includes(paymentType) && (
                  <FeeAmountTextField
                    id="cash-discount"
                    disabled
                    fee={calculationResult.fees?.cashDiscount}
                    databaseFeeSettings={userData?.settings}
                  />
                )}
              </>
            )}
            <AddressFieldsDropDown
              type="all"
              billingAddressFields={billingAddress}
              shippingAddressFields={shippingAddress}
              billingAddressSettings={userData?.settings?.virtualTerminalSettings?.billingAddress}
              shippingAddressSettings={userData?.settings?.virtualTerminalSettings?.shippingAddress}
              setBillingAddressFields={setBillingAddress}
              setShippingAddressFields={setShippingAddress}
              billingFormValidationRef={billingAddressFormValidationRef}
              shippingFormValidationRef={shippingAddressFormValidationRef}
              fieldAlignment={'column'}
            />
            {calculationResult?.options?.taxLast &&
              (isDualPricingView ? (
                <Stack spacing={2} direction="row">
                  <FeeAmountTextField
                    id="cash-tax"
                    labelPrefix="Cash"
                    disabled
                    fee={displayCalculationResult.cash?.fees?.tax}
                    databaseFeeSettings={userData?.settings}
                  />
                  <FeeAmountTextField
                    id="card-tax"
                    labelPrefix="Card"
                    disabled
                    fee={displayCalculationResult.card?.fees?.tax}
                    databaseFeeSettings={userData?.settings}
                  />
                </Stack>
              ) : (
                <FeeAmountTextField
                  id="tax"
                  disabled
                  fee={calculationResult.fees?.tax}
                  databaseFeeSettings={userData?.settings}
                />
              ))}
            {isDualPricingView ? (
              <Stack spacing={2} direction="row" id="dual-pricing-totals">
                <AmountTextField
                  id="cash-total"
                  label={'Cash Total'}
                  disabled
                  amount={displayCalculationResult.cash?.totalAmount}
                  fullWidth
                  error={fieldErrors?.totalAmount}
                  isBold={paymentTypeToggle !== 'card'}
                />
                <AmountTextField
                  id="card-total"
                  label={'Card Total'}
                  disabled
                  amount={displayCalculationResult.card?.totalAmount}
                  fullWidth
                  error={fieldErrors?.totalAmount}
                  isBold={paymentTypeToggle === 'card'}
                />
              </Stack>
            ) : (
              <AmountTextField
                id="total-amount"
                label={'Total'}
                disabled
                amount={calculationResult.totalAmount}
                fullWidth
                error={fieldErrors?.totalAmount}
                isBold
              />
            )}
            <TextField id="notes" fullWidth label="Notes" onChange={(e) => setNotes(e?.target?.value)} value={notes} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={12} id="buttons-section">
          <Stack direction="row">
            <Grid container rowSpacing={2} columnSpacing={2} justifyContent={'flex-end'} id="right-side-buttons">
              {isPaxTransactionsEnabled && (
                <Grid item xs={12} md={'auto'}>
                  <PaxTerminalModal fullWidth onSubmit={handlePaxSaleRequest} paxTerminals={paxTerminals} />
                </Grid>
              )}
              <Grid item xs={12} md={'auto'}>
                <Button
                  id="process-payment-button"
                  disabled={submitPayIsDisabled}
                  fullWidth
                  variant="contained"
                  onClick={handleSubmitPay}
                >
                  {payButtonLabel}
                </Button>
              </Grid>
              <Grid item xs={12} md={'auto'}>
                <BackButton fullWidth />
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OneTimeCustomerTab;
